import { ActionStatus } from 'thunkless';
import produce from 'immer';
import * as _ from 'lodash';

import { AppAction } from '../actions';
import { OrganizationTypes } from '../actions/organization';

export interface OrganizationState {
  loadingStatus: ActionStatus,
  usageLoadingStatus: ActionStatus,
  checkoutLoadingStatus: ActionStatus,
  stripeCheckoutStatus: ActionStatus;
  updateAutoFillStatus: ActionStatus;
  fetchPlanFeaturesLoadingStatus: ActionStatus;
  stripeCheckoutError: string;
  id: string;
  name: string;
  plan: string;
  planName: string;
  seats: number;
  seatsPending: number;
  seatsInUse: number;
  seatsAvailable: number;
  seatPrice: number;
  seatUpgradePrice: number;
  balanceOptions: number[];
  thresholdOptions: number[];
  balanceRemaining: number;
  payPeriod: string;
  autoFill: boolean;
  autoFillBalance: number;
  autoFillThreshold: number;
  stripeSessionId: string;
  stripeCustomerPortalSessionUrl: string;
  phoneNumbers: number[];
  subscriptionEnd: string;
  hasStripeSubscription: boolean;
  checkout: Record<any, any>;
  cancelSubscription: Record<any, any>;
  usage: Record<any, any>;
}

export const initialState: OrganizationState = {
  loadingStatus: null,
  usageLoadingStatus: null,
  checkoutLoadingStatus: null,
  stripeCheckoutStatus: null,
  updateAutoFillStatus: null,
  fetchPlanFeaturesLoadingStatus: null,
  stripeCheckoutError: null,
  id: null,
  name: null,
  plan: null,
  planName: null,
  seats: null,
  seatsPending: null,
  seatsInUse: null,
  seatsAvailable: null,
  seatPrice: null,
  seatUpgradePrice: null,
  balanceOptions: [],
  thresholdOptions: [],
  balanceRemaining: null,
  payPeriod: null,
  autoFill: null,
  autoFillBalance: null,
  autoFillThreshold: null,
  stripeSessionId: null,
  stripeCustomerPortalSessionUrl: null,
  phoneNumbers: [],
  subscriptionEnd: null,
  hasStripeSubscription: null,
  checkout: {
    features: null,
    processingStatus: null,
    processingMessage: null,
  },
  cancelSubscription: {
    status: null,
    message: null,
  },
  usage: null,
};

export const reducer = produce((state: OrganizationState, action: AppAction) => {
  switch (action.type) {
    case OrganizationTypes.STRIPE_CHECKOUT_REQUEST: {
      state.stripeCheckoutStatus = ActionStatus.BUSY;
      break;
    }
    case OrganizationTypes.STRIPE_CHECKOUT_SUCCESS: {
      state.stripeCheckoutStatus = ActionStatus.SUCCESS;
      const { stripeSessionId } = action.payload.data;
      _.set(state, 'stripeSessionId', stripeSessionId);
      break;
    }
    case OrganizationTypes.STRIPE_CHECKOUT_FAILURE: {
      state.stripeCheckoutStatus = ActionStatus.FAILURE;
      break;
    }
    case OrganizationTypes.STRIPE_CUSTOMER_PORTAL_SUCCESS: {
      const { stripeCustomerPortalSessionUrl } = action.payload.data;
      _.set(state, 'stripeCustomerPortalSessionUrl', stripeCustomerPortalSessionUrl);
      break;
    }
    case OrganizationTypes.STRIPE_CUSTOMER_PORTAL_FAILURE: {
      break;
    }
    case OrganizationTypes.UPDATE_AUTO_FILL_REQUEST: {
      state.updateAutoFillStatus = ActionStatus.BUSY;
      break;
    }
    case OrganizationTypes.UPDATE_AUTO_FILL_SUCCESS: {
      state.updateAutoFillStatus = ActionStatus.SUCCESS;
      state.autoFill = action.payload.data.autoFill;
      state.autoFillBalance = action.payload.data.autoFillBalance;
      state.autoFillThreshold = action.payload.data.autoFillThreshold;
      break;
    }
    case OrganizationTypes.UPDATE_AUTO_FILL_FAILURE: {
      state.updateAutoFillStatus = ActionStatus.FAILURE;
      break;
    }
    case OrganizationTypes.GET_ORG_PROPS_REQUEST: {
      state.loadingStatus = ActionStatus.BUSY;
      break;
    }
    case OrganizationTypes.GET_ORG_PROPS_SUCCESS: {
      state.loadingStatus = ActionStatus.SUCCESS;
      const datas = Object.entries(action.payload.data);
      datas.forEach((data) => _.set(state, data[0], data[1]));
      break;
    }
    case OrganizationTypes.GET_ORG_PROPS_FAILURE: {
      state.loadingStatus = ActionStatus.FAILURE;
      break;
    }
    case OrganizationTypes.GET_BALANCE_USAGE_REQUEST: {
      state.usageLoadingStatus = ActionStatus.BUSY;
      break;
    }
    case OrganizationTypes.GET_BALANCE_USAGE_SUCCESS: {
      state.usageLoadingStatus = ActionStatus.SUCCESS;
      const datas = Object.entries(action.payload.data);
      datas.forEach((data) => _.set(state, data[0], data[1]));
      break;
    }
    case OrganizationTypes.GET_BALANCE_USAGE_FAILURE: {
      state.usageLoadingStatus = ActionStatus.FAILURE;
      break;
    }
    case OrganizationTypes.GET_SEAT_SUMMARY_REQUEST: {
      state.checkoutLoadingStatus = ActionStatus.BUSY;
      break;
    }
    case OrganizationTypes.GET_SEAT_SUMMARY_SUCCESS: {
      state.checkoutLoadingStatus = ActionStatus.SUCCESS;
      _.set(state, 'checkout', action.payload.data);
      break;
    }
    case OrganizationTypes.GET_SEAT_SUMMARY_FAILURE: {
      state.checkoutLoadingStatus = ActionStatus.FAILURE;
      break;
    }
    case OrganizationTypes.GET_UPGRADE_SUMMARY_REQUEST: {
      state.checkoutLoadingStatus = ActionStatus.BUSY;
      break;
    }
    case OrganizationTypes.GET_UPGRADE_SUMMARY_SUCCESS: {
      state.checkoutLoadingStatus = ActionStatus.SUCCESS;
      _.set(state, 'checkout', action.payload.data);
      break;
    }
    case OrganizationTypes.GET_UPGRADE_SUMMARY_FAILURE: {
      state.checkoutLoadingStatus = ActionStatus.FAILURE;
      break;
    }
    case OrganizationTypes.CANCEL_SUBSCRIPTION_REQUEST: {
      state.cancelSubscription.status = ActionStatus.BUSY;
      break;
    }
    case OrganizationTypes.CANCEL_SUBSCRIPTION_SUCCESS: {
      state.cancelSubscription.status = action.payload.data.status;
      state.cancelSubscription.message = action.payload.data.message;
      break;
    }
    case OrganizationTypes.CANCEL_SUBSCRIPTION_FAILURE: {
      state.cancelSubscription.status = ActionStatus.FAILURE;
      break;
    }
    case OrganizationTypes.SET_CHECKOUT_SUMMARY: {
      state.checkoutLoadingStatus = ActionStatus.SUCCESS;
      _.set(state, 'checkout', action.payload.data);
      break;
    }
    case OrganizationTypes.SELF_SERVE_CHECKOUT_REQUEST: {
      state.checkout.processingStatus = ActionStatus.BUSY;
      break;
    }
    case OrganizationTypes.SELF_SERVE_CHECKOUT_SUCCESS: {
      state.checkout.processingResponseStatus = action.payload.data.status;
      state.checkout.processingResponseMessage = action.payload.data.message;
      break;
    }
    case OrganizationTypes.CLOSE_CHECKOUT_PROCESSING: {
      state.checkout.processingResponseStatus = '';
      state.checkout.processingResponseMessage = '';
      break;
    }
    case OrganizationTypes.CLEAR_ORG_STATUSES: {
      state.checkout.processingResponseStatus = null;
      state.checkout.processingResponseMessage = null;
      state.cancelSubscription.status = null;
      state.cancelSubscription.message = null;
      state.loadingStatus = null;
      state.usageLoadingStatus = null;
      state.checkoutLoadingStatus = null;
      state.stripeCheckoutStatus = null;
      state.updateAutoFillStatus = null;
      break;
    }
    case OrganizationTypes.SET_CHECKOUT_SEAT_QUANTITY: {
      const { seatPrice, seatProratedPrice, upcomingAmountDue } = state.checkout;
      const quantity = action.payload.data;
      const totalToday = `$${(seatProratedPrice * quantity).toFixed(2)}`;
      const singleSeatSubscription = state.payPeriod === 'monthly' ? seatPrice : seatPrice * 12;
      const newProjectedUpcomingAmountDue = `$${((
        upcomingAmountDue + (singleSeatSubscription * quantity)
      ).toFixed(2))}`;

      _.set(state, ['checkout', 'lineItems', 'seat', 'quantity'], quantity);
      _.set(state, ['checkout', 'lineItems', 'seat', 'total'], totalToday);
      _.set(state, ['checkout', 'totalToday'], totalToday);
      _.set(state, ['checkout', 'projectedUpcomingAmountDue'], newProjectedUpcomingAmountDue);
      _.set(state, ['checkout', 'seatQuantity'], quantity);
      break;
    }
    case OrganizationTypes.SET_CHECKOUT_BALANCE: {
      const unitCost = `$${action.payload.data}`;
      _.set(state, ['checkout', 'lineItems', 'balance', 'unitCost'], unitCost);
      _.set(state, ['checkout', 'lineItems', 'balance', 'total'], unitCost);
      _.set(state, ['checkout', 'totalToday'], unitCost);
      _.set(state, ['checkout', 'balance'], action.payload.data);
      break;
    }
  }
}, initialState);
